'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

const DashboardTopBar = () => {
  const router = useRouter();
  const { userProfile } = useAuth();

  // Generate initials from name
  const getInitials = (name: string | undefined | null): string => {
    if (!name) return 'ME'; // Default to "ME" for Marvel Editing
    
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  // Generate a consistent color based on the name
  const getAvatarColor = (name: string | undefined | null): string => {
    if (!name) return 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)';
    
    // Simple hash function to generate consistent colors
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    const gradients = [
      'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)', // Blue to Purple
      'linear-gradient(135deg, #10B981 0%, #059669 100%)', // Green
      'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)', // Orange
      'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)', // Red
      'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', // Purple
      'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)', // Cyan
      'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)', // Pink
      'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)', // Indigo
    ];
    
    return gradients[Math.abs(hash) % gradients.length];
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      router.push('/auth/signin');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const userName = userProfile?.full_name || 'Marvel Editing';
  const initials = getInitials(userName);
  const avatarColor = getAvatarColor(userName);

  return (
    <div 
      className="h-16 border-b flex items-center justify-between px-4 sm:px-8 backdrop-blur-sm"
      style={{ 
        backgroundColor: 'rgba(15, 16, 20, 0.85)',
        borderColor: 'rgba(31, 41, 55, 0.5)',
      }}
    >
      <div className="flex items-center gap-3">
        {/* Mobile menu toggle */}
        <button
          onClick={() => window.dispatchEvent(new Event('toggle-sidebar'))}
          className="lg:hidden p-2 -ml-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/[0.06] transition-colors"
          aria-label="Open menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h1 className="text-xl font-semibold" style={{ color: '#FFFFFF' }}>
          Dashboard
        </h1>
      </div>
      
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Notification Bell */}
        <button
          className="relative p-2 rounded-lg transition-colors hover:bg-[#1F2937]"
          style={{ color: '#9CA3AF' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span 
            className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500"
          />
        </button>

        {/* User Profile */}
        <div className="flex items-center gap-3 pl-1 sm:pl-2">
          {userProfile?.profile_picture_url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={userProfile.profile_picture_url}
              alt={userName}
              onClick={() => router.push('/client-dashboard/profile')}
              referrerPolicy="no-referrer"
              className="w-9 h-9 rounded-full object-cover cursor-pointer flex-shrink-0 border border-white/[0.12]"
            />
          ) : (
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm cursor-pointer flex-shrink-0"
              onClick={() => router.push('/client-dashboard/profile')}
              style={{
                background: avatarColor,
                color: '#FFFFFF',
              }}
            >
              {initials}
            </div>
          )}
          <div className="text-left hidden sm:block">
            <div className="text-sm font-medium" style={{ color: '#E5E7EB' }}>
              {userName}
            </div>
            <div className="text-xs" style={{ color: '#6B7280' }}>
              Client
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all text-sm font-medium"
          style={{
            backgroundColor: '#1A1B1F',
            color: '#9CA3AF',
            borderWidth: '1px',
            borderColor: '#2A2B2F',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#EF4444';
            e.currentTarget.style.color = '#FFFFFF';
            e.currentTarget.style.borderColor = '#EF4444';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#1A1B1F';
            e.currentTarget.style.color = '#9CA3AF';
            e.currentTarget.style.borderColor = '#2A2B2F';
          }}
        >
          <Icon name="ArrowRightOnRectangleIcon" size={16} variant="outline" style={{ color: 'inherit' }} />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default DashboardTopBar;
